import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AssigneeRef, ConnectionRow, FormDetail, SubmissionDetailResponse, SubmissionRow, TRIAGE_STATUSES, TriagePatch, WorkflowGraph,
  addNoteApi, deleteNoteApi, deleteSubmissionApi, getFormBuilder, getFormWorkflow, getSubmissionDetailFull,
  listAssigneesApi, listFormConnections, renderSubmissionDocument, retryDeliveryApi, setTriageApi, updateSubmissionApi,
} from '../../api/forms';
import { CatalogEntry, getIntegrationCatalog } from '../../api/admin';
import type { Submitter } from '../../shared/types';
import { normalizeRules } from '../../shared/types';
import { formatCompositeAnswer } from '../../shared/address';
import { matrixMeta, matrixText, rankingText } from '../../form-engine/questionTypes';
import { FormState, PublishedDef, stateFromData } from '../../form-engine/model';
import { buildSubmissionData } from '../../form-engine/payload';
import { evaluateForm } from '../../form-engine/evaluate';
import FormRenderer from '../../form-engine/FormRenderer';
import {
  DELIVERY_INTENT, DeliveryState, TRIAGE_INTENT, TRIAGE_LABEL, deliveryPillText, formatFull, triageOf,
} from './submissionColumns';
import SubmissionHistory from './SubmissionHistory';
import ResendDialog from './ResendDialog';
import FollowUpResendDialog from './FollowUpResendDialog';
import { ConfirmationRecord, ContinuesBanner, FollowUpTabPanel } from './FollowUpPanels';
import RunView from './workflow/RunView';
import { useToast } from '../../components/Toast';
import { TestPill } from './SubmissionsList';
import { useUnsavedGuard } from '../../components/unsaved';
import { Modal, RowMenu, TabStrip } from '../../components/fs';
import { COMPACT_HEAD, PHONE, useMediaQuery } from '../../shared/media';

type Tab = 'answers' | 'deliveries' | 'workflow' | 'followup' | 'history' | 'raw';

/**
 * One submission, over the table.
 *
 * Keeps the row's own URL so an existing deep link still works — opened that
 * way it appears over the default table rather than nothing. `↑`/`↓` move
 * through the FILTERED rows, because triage is a list you work down.
 */
/** `utm_source=newsletter · ref=partner` out of `meta.query`, or '' when the link carried nothing tracked. */
export function arrivedFrom(meta: Record<string, unknown> | null | undefined): string {
  const q = meta?.query;
  if (!q || typeof q !== 'object' || Array.isArray(q)) return '';
  return Object.entries(q as Record<string, unknown>)
    .filter(([, v]) => typeof v === 'string' && v)
    .map(([k, v]) => `${k}=${v}`)
    .join(' · ');
}

export default function SubmissionDrawer({ form, submissionId, siblings, position, editing, docked, onNavigate, onClose, onSetEditing, onChanged }: {
  form: FormDetail;
  submissionId: number;
  /** the ids currently on screen, in order — what ↑/↓ step through */
  siblings: number[];
  position: { index: number; total: number } | null;
  editing: boolean;
  /**
   * R8 (screen 19): on a desktop the panel is a locked column beside the
   * table — no scrim, no slide, the page's own height — rather than the
   * overlay drawer it is below 1100 and always was.
   */
  docked?: boolean;
  onNavigate: (id: number) => void;
  onClose: () => void;
  onSetEditing: (on: boolean) => void;
  /** the row changed underneath the table — reload it */
  onChanged: () => void;
}) {
  const [detail, setDetail] = useState<SubmissionDetailResponse | null>(null);
  const [def, setDef] = useState<PublishedDef | null>(null);
  const [tab, setTab] = useState<Tab>('answers');
  const [draft, setDraft] = useState<FormState | null>(null);
  /**
   * The payload the editor produced from the STORED answers, before anyone
   * touched it.
   *
   * "Changed" has to mean "changed by this person". Running the answers back
   * through the renderer can legitimately add values the stored data never had
   * — a total the form computes, a rule added since — and comparing the save
   * against the database would file those as somebody's edit and write a
   * revision nobody made.
   */
  const [baseline, setBaseline] = useState<Record<string, unknown> | null>(null);
  const [changed, setChanged] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const toast = useToast();
  /**
   * What the run view needs, and only when there is a run view.
   *
   * A form on the connection list has no path to draw, so `workflow` stays
   * null and the tab never appears — an empty tab on every submission would be
   * worse than no tab.
   */
  const [workflow, setWorkflow] = useState<WorkflowGraph | null>(null);
  const [connections, setConnections] = useState<ConnectionRow[]>([]);
  const [catalog, setCatalog] = useState<CatalogEntry[]>([]);
  const [resendOpen, setResendOpen] = useState(false);
  const [resendInvite, setResendInvite] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, Set<number>>>({});
  const [rendering, setRendering] = useState(false);
  /** triage (P5): who this can be handed to, the note being written, and the two saves in flight */
  const [assignees, setAssignees] = useState<AssigneeRef[]>([]);
  const [noteDraft, setNoteDraft] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [triageBusy, setTriageBusy] = useState(false);

  // The people list is per form, not per submission — one call however many
  // rows are stepped through.
  useEffect(() => {
    listAssigneesApi(form.id).then((r) => setAssignees(r.users)).catch(() => setAssignees([]));
  }, [form.id]);

  const load = useCallback(() => {
    getFormWorkflow(form.id)
      // R0 found the old check read a field the route never sends, so the
      // tab never appeared; a saved workflow is the whole condition (R6).
      .then((r) => setWorkflow(r.workflow ? r.workflow.graph : null))
      .catch(() => setWorkflow(null));
    listFormConnections(form.id).then((r) => setConnections(r.connections)).catch(() => undefined);
    getIntegrationCatalog().then((r) => setCatalog([...r.builtIn, ...r.integrations])).catch(() => undefined);
    getSubmissionDetailFull(submissionId)
      .then((r) => { setDetail(r); setError(''); })
      .catch((e) => setError(e.message));
  }, [submissionId]);
  useEffect(() => { load(); }, [load]);

  // The editor is the real renderer, so it needs the published definition.
  useEffect(() => {
    getFormBuilder(form.id)
      .then((b) => setDef({ fields: b.fields, layout: b.layout, rules: normalizeRules(b.rules), widgets: b.widgets || [] }))
      .catch(() => undefined);
  }, [form.id]);

  const submission = detail?.submission;

  /**
   * The document, as it stands for this submission.
   *
   * `sent` is what actually went out and is never rewritten — that is the
   * point of storing it. A row with an error and no url is a document that
   * could not be made; it offers a Retry rather than pretending there is one.
   */
  const documents = detail?.documents || [];
  const sentCopy = documents.find((d) => d.kind === 'sent' && d.url);
  const freshCopy = documents.find((d) => d.kind === 'rerendered' && d.url);
  const docError = documents.find((d) => d.kind === 'sent' && d.error)?.error;

  /**
   * Make a document now. The SERVER decides which job that is — the retry on a
   * submission that never got one, or a fresh copy beside the one that went
   * out — because it is the only side that knows what is already stored.
   */
  async function makeFreshCopy() {
    setRendering(true);
    try {
      const r = await renderSubmissionDocument(submissionId);
      toast(r.kind === 'sent'
        ? 'Document stored. This is the copy this submission now carries.'
        : 'A fresh copy was made. The copy that was sent is untouched.');
      load();
      onChanged();
    } catch (e: any) {
      setError(e.message || 'The document could not be made.');
    } finally {
      setRendering(false);
    }
  }

  /** Seed the editor from the stored answers, every time editing starts. */
  useEffect(() => {
    if (!editing || !def || !submission) { setDraft(null); return; }
    // Scalars and repeating groups alike, the way the public edit page does
    // it too (P6) — one hydration for both editors.
    const next = stateFromData(def, submission.data || {});
    setDraft(next);
    setBaseline(buildSubmissionData(def, next, evaluateForm(def, next, {})));
    setChanged([]);
  }, [editing, def, submission]);

  /**
   * Has anyone actually changed an answer?
   *
   * The same comparison the save runs, for the same reason: re-rendering the
   * stored answers can legitimately produce values the database never held,
   * and those are not somebody's edit. So the guard asks only when a real
   * answer differs — otherwise opening the editor and closing it again would
   * be met with a question about nothing.
   */
  const dirty = useMemo(() => {
    if (!editing || !def || !draft || !baseline) return false;
    return !sameValues(buildSubmissionData(def, draft, evaluateForm(def, draft, {})), baseline);
  }, [editing, def, draft, baseline]);

  const guard = useUnsavedGuard({
    dirty, busy: saving,
    what: 'Your edits to these answers',
    onSave: () => save(false),
  });
  const { confirm } = guard;
  /** Everything that abandons the editor goes through here: Esc, ×, Cancel, the scrim. */
  const leaveEditor = useCallback(() => confirm(() => onSetEditing(false)), [confirm, onSetEditing]);
  const closeDrawer = useCallback(() => confirm(onClose), [confirm, onClose]);

  // ↑/↓ through the filtered rows, Esc closes, e starts editing.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test((e.target as HTMLElement)?.tagName || '');
      if (e.key === 'Escape') { if (editing) leaveEditor(); else onClose(); return; }
      if (typing || resendOpen) return;
      const at = siblings.indexOf(submissionId);
      // Stepping to the next submission replaces what is in the editor, so it
      // is a way out of it too.
      if (e.key === 'ArrowDown' && at >= 0 && at < siblings.length - 1) { e.preventDefault(); confirm(() => onNavigate(siblings[at + 1])); }
      if (e.key === 'ArrowUp' && at > 0) { e.preventDefault(); confirm(() => onNavigate(siblings[at - 1])); }
      if (e.key === 'e' && !editing) { e.preventDefault(); onSetEditing(true); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [siblings, submissionId, editing, resendOpen, onNavigate, onClose, onSetEditing, leaveEditor, confirm]);

  const at = siblings.indexOf(submissionId);
  const summary = submission?.delivery_summary;
  const failed = detail?.deliveries.filter((d) => d.status === 'failed') || [];
  const firstFailure = failed[0];

  async function save(thenResend: boolean) {
    if (!def || !draft) return;
    setSaving(true); setError('');
    try {
      const ev = evaluateForm(def, draft, {});
      const data = buildSubmissionData(def, draft, ev);

      // Nothing the person touched: say so and write nothing.
      if (baseline && sameValues(data, baseline)) {
        toast('Nothing changed, so no revision was recorded.', { kind: 'info' });
        onSetEditing(false);
        return;
      }

      const r = await updateSubmissionApi(submissionId, data);
      if (r.unchanged) {
        toast('Nothing changed, so no revision was recorded.', { kind: 'info' });
        onSetEditing(false);
      } else {
        setChanged(r.changed);
        toast(`Saved. ${r.changed.length} answer${r.changed.length === 1 ? '' : 's'} changed, and the edit is in History.`);
        onSetEditing(false);
        load();
        onChanged();
        if (thenResend) setResendOpen(true);
      }
    } catch (e: any) {
      setError(e.message);
      throw e;   // so "Save and leave" stays put rather than losing the edit
    }
    finally { setSaving(false); }
  }

  async function remove() {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    try { await deleteSubmissionApi(submissionId); onChanged(); onClose(); }
    catch (e: any) { setError(e.message); setConfirmDelete(false); }
  }

  /**
   * An absent level means the server did not say, not that the answer is no
   * — the same courtesy the workspace shell extends. The routes refuse either
   * way; the pickers are only disabled so a viewer is not offered a change
   * that will bounce.
   */
  const canTriage = !detail?.level || detail.level !== 'view';

  /** Status or assignee — one call, then the row and the table both reload. */
  async function changeTriage(patch: TriagePatch) {
    setTriageBusy(true); setError('');
    try {
      const r = await setTriageApi(submissionId, patch);
      if (patch.status) toast(`Marked ${TRIAGE_LABEL[patch.status].toLowerCase()}.`);
      else if (patch.assigneeUserId === null) toast('Unassigned.');
      else if (r.assignee) toast(`Assigned to ${r.assignee.name}${r.emailed ? ' — they have been emailed.' : '.'}`);
      load();
      onChanged();
    } catch (e: any) {
      setError(e.message || 'The change could not be saved.');
    } finally {
      setTriageBusy(false);
    }
  }

  /**
   * Putting a revision's answers back (R8; R0 found `SubmissionHistory` took
   * `onRestore` and nothing passed it). It is an ordinary edit: the same
   * route, so it lands in History as a revision of its own, and nothing is
   * re-sent until somebody chooses to.
   */
  const [restoring, setRestoring] = useState<Record<string, unknown> | null>(null);
  async function restoreRevision() {
    if (!restoring) return;
    setSaving(true); setError('');
    try {
      const r = await updateSubmissionApi(submissionId, restoring);
      if (r.unchanged) toast('Those answers are what the submission already holds.', { kind: 'info' });
      else toast(`Restored. ${r.changed.length} answer${r.changed.length === 1 ? '' : 's'} changed, and the edit is in History.`);
      setRestoring(null);
      load();
      onChanged();
    } catch (e: any) {
      setError(e.message || 'The answers could not be restored.');
    } finally {
      setSaving(false);
    }
  }

  async function addNote() {
    const body = noteDraft.trim();
    if (!body) return;
    setSavingNote(true); setError('');
    try {
      await addNoteApi(submissionId, body);
      setNoteDraft('');
      load();
    } catch (e: any) {
      setError(e.message || 'The note could not be saved.');
    } finally {
      setSavingNote(false);
    }
  }

  async function removeNote(noteId: number) {
    if (!window.confirm('Delete this note? It cannot be undone.')) return;
    try { await deleteNoteApi(submissionId, noteId); load(); }
    catch (e: any) { setError(e.message || 'The note could not be deleted.'); }
  }

  const submitter = submission?.submitter as Submitter | null;
  const [resendAfterSave, setResendAfterSave] = useState(true);
  /*
    F2' — the phone drawer. Under 700px the head keeps the title row and the
    two pills and folds the other five blocks into one disclosure, so the tabs
    are on the first screen instead of below it; under 600px the footer's six
    actions become two and a sheet. Every keyboard path (Esc, ↑/↓, e), the
    unsaved guard and every handler are untouched.
  */
  const compactHead = useMediaQuery(COMPACT_HEAD);
  const phone = useMediaQuery(PHONE);
  const [moreOpen, setMoreOpen] = useState(false);

  return (
    <>
      {!docked && <div className="sl-scrim" onClick={() => (editing ? leaveEditor() : onClose())} />}
      <div className={`sl-drawer${docked ? ' docked' : ''}`} role={docked ? undefined : 'dialog'} aria-label={docked ? undefined : (submission?.submission_id || 'Response')} onClick={() => confirmDelete && setConfirmDelete(false)}>
        <div className="sl-dhead">
          <div className="sl-dtitle">
            <h2 title={submission?.submission_id}>{submission?.submission_id || '…'}</h2>
            {submission?.is_test && <TestPill />}
            {editing
              ? <span className="fs-pill identity">editing</span>
              : summary && (
                <span className={`fs-pill ${DELIVERY_INTENT[summary.state as DeliveryState]}`}>
                  {deliveryPillText({ ...summary, state: summary.state as DeliveryState })}
                </span>
              )}
            {!editing && submission?.payment && (() => {
              const pay = submission.payment;
              // A refund made from Settings → Payments (R11) is noted on the record.
              const refunded = pay.status === 'paid' ? Math.min(pay.refundedCents || 0, pay.amountCents) : 0;
              const whole = refunded > 0 && refunded >= pay.amountCents;
              return (
                <span
                  className={`fs-pill ${whole ? 'neutral' : pay.status === 'paid' ? 'ok' : 'warn'}`}
                  title={pay.paidAt ? `Paid ${formatFull(pay.paidAt)}${pay.refundedAt && refunded ? ` · refunded ${formatFull(pay.refundedAt)}` : ''}` : 'Payment not completed yet'}
                >
                  💳 {(pay.amountCents / 100).toFixed(2)} {pay.currency.toUpperCase()}
                  {' · '}{whole ? 'refunded' : pay.status === 'paid' ? 'paid' : pay.status}
                  {refunded > 0 && !whole && ` · ${(refunded / 100).toFixed(2)} refunded`}
                </span>
              );
            })()}
            <span style={{ flex: 1 }} />
            {editing ? (
              <span className="sl-dpos">
                {/* The detail response carries the revision list; the list
                    endpoint's last_revision is not part of it. */}
                Revision {(detail?.revisions[0]?.revision || 1) + 1} · draft
              </span>
            ) : (
              <>
                {position && <span className="sl-dpos">{position.index} of {position.total}</span>}
                <button className="sl-step" title="Previous (↑)" disabled={at <= 0} onClick={() => confirm(() => onNavigate(siblings[at - 1]))}>↑</button>
                <button className="sl-step" title="Next (↓)" disabled={at < 0 || at >= siblings.length - 1} onClick={() => confirm(() => onNavigate(siblings[at + 1]))}>↓</button>
              </>
            )}
            <button className="sl-step sl-close" title="Close (Esc)" onClick={closeDrawer}>×</button>
          </div>

          {/* F2': the same five blocks, behind one summary line on a phone.
              `compactHead` is false at every desktop width, so the head below
              700px is the only place a disclosure exists. */}
          {compactHead ? (
            <details className="sl-about">
              <summary>About this submission</summary>
            {submission && (
              <div className="sl-dmeta">Received {formatFull(submission.created_at)} · {form.name}{submission.payment?.status === 'paid' ? ` · paid ${(submission.payment.amountCents / 100).toFixed(2)} ${submission.payment.currency.toUpperCase()}` : ''}</div>
            )}

            {/*
              Triage (P5): where this is and who has it, as the two things a
              person changes most — a pill that is also the picker, and the
              assignee beside it. Both write straight through; there is no save
              button because there is nothing to compose.
            */}
            {submission && !editing && (
              <div className="sl-triage" data-testid="triage-row">
                <label>
                  <span>Status</span>
                  <select
                    aria-label="Triage status"
                    className={`fs-pill ${TRIAGE_INTENT[triageOf(submission)]} sl-triage-pick`}
                    value={triageOf(submission)}
                    disabled={!canTriage || triageBusy}
                    onChange={(e) => void changeTriage({ status: e.target.value as (typeof TRIAGE_STATUSES)[number] })}
                  >
                    {TRIAGE_STATUSES.map((s) => <option key={s} value={s}>{TRIAGE_LABEL[s]}</option>)}
                  </select>
                </label>
                <label>
                  <span>Assigned to</span>
                  <select
                    aria-label="Assignee"
                    className="fs-input"
                    value={detail?.assignee?.id ?? submission.assignee_user_id ?? ''}
                    disabled={!canTriage || triageBusy}
                    onChange={(e) => void changeTriage({ assigneeUserId: e.target.value ? Number(e.target.value) : null })}
                  >
                    <option value="">Nobody</option>
                    {assignees.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                    {/* An assignee who has since lost the form stays selectable, so the row still says who has it. */}
                    {detail?.assignee && !assignees.some((u) => u.id === detail.assignee!.id) && (
                      <option value={detail.assignee.id}>{detail.assignee.name}</option>
                    )}
                  </select>
                </label>
                {!canTriage && <span>View access — ask an Admin for Edit to change these.</span>}
              </div>
            )}
            {/* Tracking params the link carried (P1 / A5): utm_* and friends, kept on meta, never an answer. */}
            {submission && arrivedFrom(submission.meta) && (
              <div className="sl-dmeta" title="Query parameters on the link the respondent opened">
                Arrived from {arrivedFrom(submission.meta)}
              </div>
            )}

            {submission && (
              submitter ? (
                <div className="sl-identity">
                  <span className="fs-pill ok">
                    ✓ Verified via {submitter.kind === 'magic_link' ? 'email' : 'SSO'}
                  </span>
                  <span>
                    {[submitter.name, submitter.email, submitter.providerName].filter(Boolean).join(' · ')}
                  </span>
                </div>
              ) : (
                <div className="sl-identity plain">
                  <span className="sl-pill ad-pill muted">No identity captured</span>
                  <span>This form was open or password-gated when it was submitted.</span>
                </div>
              )
            )}

            {detail?.continues && <ContinuesBanner continues={detail.continues} />}

            {/*
              Which document this submission has, said plainly.

              "As sent" is the fact that matters: the bytes are the ones that
              went out, and editing the answers or the form's document afterwards
              does not touch them. A submission taken before documents were
              stored has none, and says so rather than implying the download
              below is a copy of anything — it is rendered from the document as
              it stands today, which is a different claim.
            */}
            {detail && (
              <div className="sl-identity plain">
                {sentCopy ? (
                  <>
                    <span className="sl-pill ad-pill">Document as sent</span>
                    <span>
                      {new Date(sentCopy.rendered_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                      {sentCopy.pages ? ` · ${sentCopy.pages} page${sentCopy.pages === 1 ? '' : 's'}` : ''}
                      {freshCopy && ' · a fresh copy was made since, and is kept beside it'}
                    </span>
                  </>
                ) : docError ? (
                  <>
                    <span className="sl-pill ad-pill danger">Document not stored</span>
                    <span>{docError} — nothing was sent with this submission. Retry it below.</span>
                  </>
                ) : (
                  <>
                    <span className="sl-pill ad-pill muted">No document stored</span>
                    <span>
                      Taken before documents were kept. Downloading renders one now, from the document as
                      it stands today — not the copy that was sent.
                    </span>
                  </>
                )}
              </div>
            )}
            </details>
          ) : (
            <>
            {submission && (
              <div className="sl-dmeta">Received {formatFull(submission.created_at)} · {form.name}{submission.payment?.status === 'paid' ? ` · paid ${(submission.payment.amountCents / 100).toFixed(2)} ${submission.payment.currency.toUpperCase()}` : ''}</div>
            )}

            {/*
              Triage (P5): where this is and who has it, as the two things a
              person changes most — a pill that is also the picker, and the
              assignee beside it. Both write straight through; there is no save
              button because there is nothing to compose.
            */}
            {submission && !editing && (
              <div className="sl-triage" data-testid="triage-row">
                <label>
                  <span>Status</span>
                  <select
                    aria-label="Triage status"
                    className={`fs-pill ${TRIAGE_INTENT[triageOf(submission)]} sl-triage-pick`}
                    value={triageOf(submission)}
                    disabled={!canTriage || triageBusy}
                    onChange={(e) => void changeTriage({ status: e.target.value as (typeof TRIAGE_STATUSES)[number] })}
                  >
                    {TRIAGE_STATUSES.map((s) => <option key={s} value={s}>{TRIAGE_LABEL[s]}</option>)}
                  </select>
                </label>
                <label>
                  <span>Assigned to</span>
                  <select
                    aria-label="Assignee"
                    className="fs-input"
                    value={detail?.assignee?.id ?? submission.assignee_user_id ?? ''}
                    disabled={!canTriage || triageBusy}
                    onChange={(e) => void changeTriage({ assigneeUserId: e.target.value ? Number(e.target.value) : null })}
                  >
                    <option value="">Nobody</option>
                    {assignees.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                    {/* An assignee who has since lost the form stays selectable, so the row still says who has it. */}
                    {detail?.assignee && !assignees.some((u) => u.id === detail.assignee!.id) && (
                      <option value={detail.assignee.id}>{detail.assignee.name}</option>
                    )}
                  </select>
                </label>
                {!canTriage && <span>View access — ask an Admin for Edit to change these.</span>}
              </div>
            )}
            {/* Tracking params the link carried (P1 / A5): utm_* and friends, kept on meta, never an answer. */}
            {submission && arrivedFrom(submission.meta) && (
              <div className="sl-dmeta" title="Query parameters on the link the respondent opened">
                Arrived from {arrivedFrom(submission.meta)}
              </div>
            )}

            {submission && (
              submitter ? (
                <div className="sl-identity">
                  <span className="fs-pill ok">
                    ✓ Verified via {submitter.kind === 'magic_link' ? 'email' : 'SSO'}
                  </span>
                  <span>
                    {[submitter.name, submitter.email, submitter.providerName].filter(Boolean).join(' · ')}
                  </span>
                </div>
              ) : (
                <div className="sl-identity plain">
                  <span className="sl-pill ad-pill muted">No identity captured</span>
                  <span>This form was open or password-gated when it was submitted.</span>
                </div>
              )
            )}

            {detail?.continues && <ContinuesBanner continues={detail.continues} />}

            {/*
              Which document this submission has, said plainly.

              "As sent" is the fact that matters: the bytes are the ones that
              went out, and editing the answers or the form's document afterwards
              does not touch them. A submission taken before documents were
              stored has none, and says so rather than implying the download
              below is a copy of anything — it is rendered from the document as
              it stands today, which is a different claim.
            */}
            {detail && (
              <div className="sl-identity plain">
                {sentCopy ? (
                  <>
                    <span className="sl-pill ad-pill">Document as sent</span>
                    <span>
                      {new Date(sentCopy.rendered_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                      {sentCopy.pages ? ` · ${sentCopy.pages} page${sentCopy.pages === 1 ? '' : 's'}` : ''}
                      {freshCopy && ' · a fresh copy was made since, and is kept beside it'}
                    </span>
                  </>
                ) : docError ? (
                  <>
                    <span className="sl-pill ad-pill danger">Document not stored</span>
                    <span>{docError} — nothing was sent with this submission. Retry it below.</span>
                  </>
                ) : (
                  <>
                    <span className="sl-pill ad-pill muted">No document stored</span>
                    <span>
                      Taken before documents were kept. Downloading renders one now, from the document as
                      it stands today — not the copy that was sent.
                    </span>
                  </>
                )}
              </div>
            )}
            </>
          )}
          {!editing && (
            <TabStrip as="div" className="sl-dtabs">
              {([['answers', 'Answers'], ['deliveries', 'Deliveries'],
                // Only where there is a drawing to show. A form on the
                // connection list has no path to draw, and an empty tab that
                // appears on every submission is worse than no tab.
                ...(workflow ? [['workflow', 'Workflow'] as [Tab, string]] : []),
                ...(detail?.followUp ? [['followup', 'Follow-up'] as [Tab, string]] : []),
                ['history', 'History'], ['raw', 'Raw JSON']] as [Tab, string][]).map(([k, label]) => (
                <button key={k} className={`sl-dtab ${tab === k ? 'on' : ''}`} onClick={() => setTab(k)}>
                  {label}
                  {k === 'deliveries' && failed.length > 0 && <span className="badge">{failed.length}</span>}
                  {k === 'history' && (detail?.notes?.length || 0) > 0 && (
                    <span className="badge notes">{detail!.notes!.length}</span>
                  )}
                </button>
              ))}
            </TabStrip>
          )}
        </div>

        <div className="sl-dbody">
          {error && <div className="ad-err">{error}</div>}

          {editing ? (
            <>
              <div className="sl-edit-banner">
                <b>Every change is recorded.</b> The original answers stay readable in History, with your name and the time
                against each edit. Nothing is re-sent until you choose to.
              </div>
              {detail?.lockedFields.length ? (
                <div className="fc-note" style={{ marginBottom: 12 }}>
                  {detail.lockedFields.length} field{detail.lockedFields.length === 1 ? '' : 's'} came from the signed-in
                  account and cannot be edited here.
                </div>
              ) : null}
              {def && draft ? (
                <FormRenderer
                  def={def}
                  state={draft}
                  onState={(update) => setDraft((s) => (s ? update(s) : s))}
                  cfg={{}}
                  formName={form.name}
                  signaturesLocked
                  idPrefix="ed"
                  accountLocked={detail?.lockedFields.length
                    ? { fields: detail.lockedFields, providerName: submitter?.providerName }
                    : undefined}
                />
              ) : <div className="sl-quiet">Loading the form…</div>}
            </>
          ) : !detail ? (
            <div className="sl-quiet">Loading…</div>
          ) : tab === 'answers' ? (
            <>
              {/* The failure is why the row was opened, so it sits above the
                  answers rather than one tab away. */}
              {firstFailure && (
                <div className="sl-fail">
                  <div className="body">
                    <b>{firstFailure.connection_type} never received this.</b>
                    <code>
                      {firstFailure.last_error}
                      {firstFailure.attempts > 1 ? ` (attempt ${firstFailure.attempts}` : ''}
                      {firstFailure.attempts > 1 ? `, last ${formatFull(firstFailure.created_at)})` : ''}
                    </code>
                  </div>
                  <button className="fs-btn primary sm" onClick={() => setResendOpen(true)}>Re-send…</button>
                </div>
              )}
              {changed.length > 0 && (
                <div className="ad-ok">Saved. Changed: {changed.join(', ')}.</div>
              )}
              {/* What the person was shown and what they said about it, above
                  the answers rather than inside them: it is a different KIND
                  of fact — a verdict, not a value. */}
              {detail.continues && submission && (
                <ConfirmationRecord continues={detail.continues} at={submission.created_at} />
              )}
              <Answers def={def} data={submission?.data || {}} expanded={expanded} setExpanded={setExpanded} />
            </>
          ) : tab === 'deliveries' ? (
            <table className="ad-table sl-deliv" style={{ fontSize: 13 }}>
              <thead><tr><th>Destination</th><th>Status</th><th>Attempts</th><th>When</th><th>Result</th></tr></thead>
              <tbody>
                {detail.deliveries.map((d) => (
                  <tr key={d.id}>
                    <td data-label="Destination">{d.connection_type}</td>
                    <td data-label="Status">{d.status}</td>
                    <td data-label="Attempts">{d.attempts}</td>
                    <td data-label="When" style={{ whiteSpace: 'nowrap' }}>{formatFull(d.created_at)}</td>
                    <td data-label="Result" style={{ wordBreak: 'break-word' }}>
                      {d.last_error
                        ? <span className="sl-bad">{d.last_error}</span>
                        : <span className="sl-quiet">{JSON.stringify(d.response || {}).slice(0, 120)}</span>}
                    </td>
                  </tr>
                ))}
                {detail.deliveries.length === 0 && (
                  <tr><td colSpan={5} className="sl-quiet">Nothing was sent — this form had no enabled connections at the time.</td></tr>
                )}
              </tbody>
            </table>
          ) : tab === 'workflow' && workflow ? (
            <RunView
              graph={workflow}
              data={submission?.data || {}}
              deliveries={detail.deliveries}
              connections={connections}
              catalog={catalog}
              onRetry={(id) => {
                // Straight through `runOneDelivery`, exactly as the Deliveries
                // tab's retry does — the drawing is a second way in, never a
                // second code path.
                retryDeliveryApi(id).then(onChanged).catch((e) => setError(e.message));
              }}
            />
          ) : tab === 'followup' && detail.followUp ? (
            <FollowUpTabPanel
              followUp={detail.followUp}
              copied={copiedLink}
              onCopy={() => {
                const url = detail.followUp?.invite?.url;
                if (!url) return;
                navigator.clipboard?.writeText(url).then(
                  () => { setCopiedLink(true); window.setTimeout(() => setCopiedLink(false), 2000); },
                  () => setError('Could not copy the link to the clipboard.')
                );
              }}
              onResend={() => setResendInvite(true)}
            />
          ) : tab === 'history' ? (
            <>
              {/* Notes go in at the top and land in the thread below, dated
                  and signed, between the edits and the deliveries they
                  usually explain. */}
              {canTriage && (
                <div className="sl-note-compose">
                  <textarea
                    className="fs-input"
                    aria-label="Add a note"
                    placeholder="Add a note for the team — what you did, what they said, what is next…"
                    value={noteDraft}
                    maxLength={4000}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); void addNote(); }
                    }}
                  />
                  <div className="row">
                    <button className="fs-btn primary sm" disabled={savingNote || !noteDraft.trim()} onClick={() => void addNote()}>
                      {savingNote ? 'Saving…' : 'Add note'}
                    </button>
                    <span>Ctrl+Enter to add. Notes stay with the team — never exported, delivered or printed.</span>
                  </div>
                </div>
              )}
              <SubmissionHistory
                submission={detail.submission}
                deliveries={detail.deliveries}
                revisions={detail.revisions}
                notes={detail.notes || []}
                onDeleteNote={canTriage ? (id) => void removeNote(id) : undefined}
                onRestore={canTriage ? (data) => setRestoring(data) : undefined}
              />
            </>
          ) : (
            <pre className="sl-raw">
              {JSON.stringify(detail.submission, null, 2)}
            </pre>
          )}
        </div>

        <div className="sl-dfoot">
          {editing ? (
            <>
              <label className="fs-check sl-resend-after">
                <input type="checkbox" checked={resendAfterSave} onChange={(e) => setResendAfterSave(e.target.checked)} />
                <span>Re-send after saving</span>
              </label>
              <span style={{ flex: 1 }} />
              <button className="fs-btn neutral sm" onClick={leaveEditor}>Cancel</button>
              <button className="fs-btn primary sm" disabled={saving}
                onClick={() => { void save(resendAfterSave).catch(() => undefined); }}>
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </>
          ) : (
            <>
              <button className="fs-btn primary sm" onClick={() => onSetEditing(true)}>Edit submission</button>
              <button className="fs-btn neutral sm" onClick={() => setResendOpen(true)}>Re-send…</button>
              {/* F2': the documents and Delete move behind one ⋯ on a phone —
                  the same links and the same handlers, one tap further in.
                  A plain link: cookie auth rides along and the browser saves
                  the file. */}
              {phone ? (
                <>
                  <span style={{ flex: 1 }} />
                  <RowMenu open={moreOpen} onToggle={setMoreOpen} label={submission?.submission_id || 'This submission'}>
                    <a href={`/api/admin/submissions/${submissionId}/export.pdf`} download>Download PDF</a>
                    {freshCopy && (
                      <a href={`/api/admin/submissions/${submissionId}/export.pdf?kind=rerendered`} download>
                        Download the fresh copy
                      </a>
                    )}
                    {sentCopy && (
                      <button disabled={rendering} onClick={() => void makeFreshCopy()}>
                        {rendering ? 'Rendering…' : 'Render a fresh copy'}
                      </button>
                    )}
                    {docError && canTriage && (
                      <button disabled={rendering} onClick={() => void makeFreshCopy()}>
                        {rendering ? 'Retrying…' : 'Retry the document'}
                      </button>
                    )}
                    {submission && canTriage && triageOf(submission) !== 'done' && (
                      <button disabled={triageBusy} onClick={() => void changeTriage({ status: 'done' })}>✓ Mark done</button>
                    )}
                    <div className="rule" />
                    <button className="danger" onClick={(e) => { e.stopPropagation(); remove(); }}>
                      {confirmDelete ? 'Confirm…' : 'Delete'}
                    </button>
                  </RowMenu>
                </>
              ) : (
                <>
                  <a className="fs-btn neutral sm" href={`/api/admin/submissions/${submissionId}/export.pdf`} download>↓ Download PDF</a>
                  {freshCopy && (
                    <a className="fs-btn neutral sm"
                      href={`/api/admin/submissions/${submissionId}/export.pdf?kind=rerendered`} download>
                      Download the fresh copy
                    </a>
                  )}
                  {sentCopy && canTriage && (
                    <button className="fs-btn neutral sm" disabled={rendering} onClick={() => void makeFreshCopy()}>
                      {rendering ? 'Rendering…' : 'Render a fresh copy'}
                    </button>
                  )}
                  {docError && canTriage && (
                    <button className="fs-btn neutral sm" disabled={rendering} onClick={() => void makeFreshCopy()}>
                      {rendering ? 'Retrying…' : 'Retry the document'}
                    </button>
                  )}
                  {/* The design's one-click close of the everyday case (screen 19). */}
                  {submission && canTriage && triageOf(submission) !== 'done' && (
                    <button className="fs-btn neutral sm" disabled={triageBusy} onClick={() => void changeTriage({ status: 'done' })}>✓ Mark done</button>
                  )}
                  <span style={{ flex: 1 }} />
                  <button className="fs-btn neutral sm sl-delete" onClick={(e) => { e.stopPropagation(); remove(); }}>
                    {confirmDelete ? 'Confirm…' : 'Delete'}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {resendOpen && detail && (
        <ResendDialog
          submissionId={submissionId}
          submissionRef={detail.submission.submission_id}
          connections={detail.connections}
          onClose={() => setResendOpen(false)}
          onSent={() => { load(); onChanged(); }}
        />
      )}

      {resendInvite && detail?.followUp && (
        <FollowUpResendDialog
          submissionId={submissionId}
          submissionRef={detail.submission.submission_id}
          followUp={detail.followUp}
          onClose={() => setResendInvite(false)}
          onSent={() => { setResendInvite(false); load(); onChanged(); }}
        />
      )}
      {restoring && (
        <Modal
          title="Restore these answers?" narrow onClose={() => setRestoring(null)}
          footer={<>
            <button className="fs-btn primary" disabled={saving} onClick={() => void restoreRevision()}>{saving ? 'Restoring…' : 'Restore'}</button>
            <button className="fs-btn neutral" onClick={() => setRestoring(null)}>Cancel</button>
          </>}
        >
          <p className="sl-restore-note">
            The answers as they stood at that revision are written back as a new revision, with your name and the
            time against it in History. Nothing is re-sent until you choose to.
          </p>
        </Modal>
      )}
      {guard.node}
    </>
  );
}

/** Deep equality that ignores key order, matching the server's own check. */
function sameValues(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null || b == null) return a === b;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((x, i) => sameValues(x, b[i]));
  }
  if (typeof a === 'object' && typeof b === 'object') {
    const ka = Object.keys(a as object).sort();
    const kb = Object.keys(b as object).sort();
    if (ka.length !== kb.length || ka.some((k, i) => k !== kb[i])) return false;
    return ka.every((k) => sameValues((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]));
  }
  return false;
}

/**
 * The answers, labelled with the field's own label rather than its key — the
 * key stays available on hover for whoever is matching this against a payload.
 */
function Answers({ def, data, expanded, setExpanded }: {
  def: PublishedDef | null;
  data: Record<string, unknown>;
  expanded: Record<string, Set<number>>;
  setExpanded: (v: Record<string, Set<number>>) => void;
}) {
  const labels = useMemo(() => {
    const out: Record<string, string> = {};
    for (const f of def?.fields || []) out[f.key] = f.label || f.key;
    return out;
  }, [def]);

  const groupLabel = (scope: string) => {
    for (const w of def?.widgets || []) {
      const find = (n: any): string | null =>
        n.scope === scope ? (n.itemLabel || scope) : (n.children || []).map(find).find(Boolean) || null;
      const hit = w.tree ? find(w.tree) : null;
      if (hit) return hit;
    }
    return scope;
  };

  const scalars = Object.entries(data).filter(([, v]) => !(Array.isArray(v) && v.every((x) => x && typeof x === 'object')));
  const groups = Object.entries(data).filter(([, v]) => Array.isArray(v) && v.every((x) => x && typeof x === 'object')) as [string, Record<string, unknown>[]][];

  const typeOf = (key: string) => def?.fields.find((f) => f.key === key);
  const show = (v: unknown, key?: string): string => {
    if (v === undefined || v === null || v === '') return '—';
    // P3: a matrix reads by its row and column labels, a ranking as a numbered line.
    const f = key ? typeOf(key) : undefined;
    if (f?.input_type === 'matrix') return matrixText(v, matrixMeta(f.ui_meta)) || '—';
    if (f?.input_type === 'ranking') return rankingText(v, f.options) || '—';
    if (Array.isArray(v)) return v.join(', ');
    if (typeof v === 'object') return formatCompositeAnswer(v) ?? JSON.stringify(v);
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    return String(v);
  };

  const summarise = (inst: Record<string, unknown>) =>
    Object.values(inst)
      .filter((v) => v !== null && v !== undefined && v !== '' && typeof v !== 'object')
      .slice(0, 3).map(String).join(' · ') || '(empty)';

  return (
    <>
      {scalars.length > 0 && (
        <>
          <div className="sl-group">Order</div>
          <table className="sl-answers">
            <tbody>
              {scalars.map(([k, v]) => (
                <tr key={k}>
                  <th title={k}>{labels[k] || k}</th>
                  <td>{show(v, k)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {groups.map(([scope, rows]) => {
        const open = expanded[scope] || new Set([0]);
        return (
          <div key={scope}>
            <div className="sl-group">{groupLabel(scope)} · {rows.length}</div>
            {rows.map((inst, i) => (
              open.has(i) ? (
                <div className="sl-inst" key={i}>
                  <div className="sl-inst-n">#{i + 1}</div>
                  <table className="sl-answers">
                    <tbody>
                      {Object.entries(inst)
                        .filter(([, v]) => !(Array.isArray(v) && v.every((x) => x && typeof x === 'object')))
                        .map(([k, v]) => (
                          <tr key={k}><th title={k}>{labels[k] || k}</th><td>{show(v, k)}</td></tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div
                  className="sl-inst-sum"
                  key={i}
                  onClick={() => setExpanded({ ...expanded, [scope]: new Set([...open, i]) })}
                >
                  #{i + 1} {summarise(inst)}
                </div>
              )
            ))}
          </div>
        );
      })}

      {scalars.length === 0 && groups.length === 0 && (
        <div className="sl-quiet">This submission has no answers.</div>
      )}
    </>
  );
}
